<template>
  <div class="pa-4" style="position: relative; min-height: 60vh">
    <v-overlay
      :value="loading"
      absolute
      color="white"
      opacity="0.75"
      z-index="6"
    >
      <v-progress-circular indeterminate color="primary" size="64" width="5" />
    </v-overlay>

    <breadcrumb />

    <!-- Header -->
    <div class="d-flex flex-wrap align-center justify-space-between mb-4">
      <div class="d-flex align-center flex-wrap">
        <h2 class="black--text mr-4 mb-0">
          {{ $t("reports.aiClient.dashTitle") }}
        </h2>
        <v-btn rounded color="primary" depressed @click="guideDialog = true">
          <v-icon left small>mdi-help-circle-outline</v-icon
          >{{ $t("reports.aiClient.dashGuide") }}
        </v-btn>
      </div>
      <credits-summary-card
        :summary="summary"
        :can-manage="canManage"
        @history="historyDialog = true"
        @manage="goManage"
      />
    </div>

    <!-- AI Report Builders -->
    <v-card outlined class="pa-5 mb-4">
      <h3 class="black--text mb-4">{{ $t("reports.aiClient.builders") }}</h3>
      <v-row class="builders-row">
        <v-col
          v-for="b in builders"
          :key="b.type"
          cols="6"
          sm="4"
          class="builders-col"
        >
          <v-card
            outlined
            class="builder-card d-flex flex-column align-center justify-center text-center pa-4"
            :class="{ 'builder-disabled': !b.route }"
            @click="openBuilder(b)"
          >
            <img
              :src="iconUrl(b.svg)"
              class="builder-icon mb-3"
              :alt="builderLabel(b)"
            />
            <div class="font-weight-medium black--text">
              {{ builderLabel(b) }}
            </div>
          </v-card>
        </v-col>
      </v-row>
    </v-card>

    <v-row>
      <!-- Report Type Distribution -->
      <v-col cols="12" md="6">
        <v-card outlined class="pa-5" style="height: 100%">
          <h3 class="black--text mb-4">
            {{ $t("reports.aiClient.distributionTitle") }}
          </h3>
          <div
            class="d-flex justify-space-between grey--text text-caption px-1 mb-1"
          >
            <span>{{ $t("reports.aiClient.colReportType") }}</span>
            <span>{{ $t("reports.aiClient.colReportsGenerated") }}</span>
          </div>
          <v-divider class="mb-1" />
          <div
            v-for="d in distribution"
            :key="d.reportType"
            class="d-flex align-center justify-space-between py-3 dist-row"
          >
            <div class="d-flex align-center">
              <img
                :src="iconUrl(svgFor(d.reportType))"
                class="dist-icon mr-3"
                :alt="labelFor(d.reportType)"
              />
              <span class="black--text">{{ labelFor(d.reportType) }}</span>
            </div>
            <span class="black--text font-weight-medium">{{
              formatNum(d.count)
            }}</span>
          </div>
        </v-card>
      </v-col>

      <!-- Recent AI Reports -->
      <v-col cols="12" md="6">
        <v-card outlined class="pa-5" style="height: 100%">
          <h3 class="black--text mb-4">
            {{ $t("reports.aiClient.recentTitle") }}
          </h3>
          <div v-if="!loading && !recent.length" class="grey--text py-4">
            {{ $t("reports.aiClient.noRecent") }}
          </div>
          <component
            :is="reportLink(r) ? 'router-link' : 'div'"
            v-for="(r, i) in recent"
            :key="i"
            :to="reportLink(r)"
            class="d-flex justify-space-between py-3 recent-row text-decoration-none"
            :class="{ 'recent-clickable': reportLink(r) }"
          >
            <div>
              <div class="font-weight-medium black--text">
                {{ labelFor(r.reportType) }}
              </div>
              <div class="grey--text text-caption">
                {{ r.entity || r.description || "-" }}
              </div>
            </div>
            <div
              class="grey--text text-caption text-right"
              style="white-space: nowrap"
            >
              {{ timeAgo(r.date) }}
            </div>
          </component>
        </v-card>
      </v-col>
    </v-row>

    <credits-history-dialog
      :value="historyDialog"
      :cooperative="summary.cooperative"
      @input="historyDialog = $event"
    />
    <dashboard-guide-dialog
      :value="guideDialog"
      @input="guideDialog = $event"
    />
  </div>
</template>

<script>
import AiCreditService from "@/_services/AiCreditService";
import creditFormat from "@/mixins/creditFormat";
import CreditsSummaryCard from "./components/CreditsSummaryCard.vue";
import CreditsHistoryDialog from "./components/CreditsHistoryDialog.vue";
import DashboardGuideDialog from "./components/DashboardGuideDialog.vue";

// Report builders in dashboard order. `route` is a named route; null when no
// builder screen exists yet (rendered but disabled). `svg` lives in
// public/icons/ai-reports/.
const BUILDERS = [
  {
    type: "vegetation_vigor",
    svg: "vegetation-vigour.svg",
    route: "cropProgressionList",
    label: "bVegetationVigor",
  },
  {
    type: "farm_productivity",
    svg: "farm-productivity.svg",
    route: "farmProductivityList",
    label: "bFarmProductivity",
  },
  {
    type: "anomaly_detection",
    svg: "anomaly-detection.svg",
    route: "anomalyDetectionList",
    label: "bAnomalies",
  },
  {
    type: "yield_prediction",
    svg: "yield-prediction.svg",
    route: "yieldPredictionList",
    label: "bYield",
  },
  // {
  //   type: "disease_detection",
  //   svg: "disease-detection.svg",
  //   route: null,
  //   label: "bDisease",
  // },
  {
    type: "land_suitability",
    svg: "land-suitability.svg",
    route: "landSuitabilityDesignList",
    label: "bLand",
  },
];

export default {
  name: "AiReportsDashboard",
  mixins: [creditFormat],
  components: {
    CreditsSummaryCard,
    CreditsHistoryDialog,
    DashboardGuideDialog,
  },
  data() {
    return {
      summary: { available: 0, total: 0, used: 0, cooperative: "" },
      distribution: [],
      recent: [],
      loading: false,
      historyDialog: false,
      guideDialog: false,
      builders: BUILDERS,
      publicPath: process.env.BASE_URL || "/",
    };
  },
  computed: {
    // Credit config is parent-cooperative-admin only. Sub-org (union/society)
    // admins get a read-only, sub-org-scoped dashboard — no Manage action.
    canManage() {
      const user = this.$store.getters.getUser || {};
      return !(user.subOrg && user.subOrg.id);
    },
  },
  created() {
    this.fetch();
  },
  methods: {
    async fetch() {
      this.loading = true;
      try {
        const env = await AiCreditService.clientDashboard({ recentLimit: 5 });
        const payload = (env && env.data) || {};
        this.summary = payload.summary || this.summary;
        this.distribution = payload.distribution || [];
        this.recent = payload.recent || [];
      } catch (e) {
        this.$notify({
          title: this.$t("reports.aiClient.errorTitle"),
          text: this.$t("reports.aiClient.loadFailed"),
          type: "error",
        });
      } finally {
        this.loading = false;
      }
    },
    iconUrl(svg) {
      return `${this.publicPath}icons/ai-reports/${svg}`;
    },
    svgFor(reportType) {
      const b = BUILDERS.find((x) => x.type === reportType);
      return b ? b.svg : "ai-fill.svg";
    },
    labelFor(reportType) {
      const b = BUILDERS.find((x) => x.type === reportType);
      return b ? this.$t(`reports.aiClient.${b.label}`) : reportType;
    },
    builderLabel(b) {
      return this.$t(`reports.aiClient.${b.label}`);
    },
    timeAgo(v) {
      if (!v) return "-";
      const then = new Date(v).getTime();
      const diffMs = Date.now() - then;
      const mins = Math.floor(diffMs / 60000);
      if (mins < 1) return "just now";
      if (mins < 60) return `${mins}m ago`;
      const hours = Math.floor(mins / 60);
      if (hours < 24) return `${hours}h ago`;
      // Older than a day: show the formatted date + time.
      const d = new Date(v);
      const p = (n) => String(n).padStart(2, "0");
      return `${this.$options.filters.formatDate(v)} | ${p(d.getHours())}:${p(
        d.getMinutes()
      )}:${p(d.getSeconds())}`;
    },
    openBuilder(b) {
      if (b.route) this.$router.push({ name: b.route });
    },
    // Detail-page route for a recent report, keyed by type. Satellite reports use
    // reportUid; yield/land use the numeric id. Returns null when no detail page
    // exists (e.g. disease detection) or the identifier is missing.
    reportLink(r) {
      switch (r.reportType) {
        case "vegetation_vigor":
          return r.reportUid
            ? { name: "cropProgressionView", params: { reportUid: r.reportUid } }
            : null;
        case "farm_productivity":
          return r.reportUid
            ? { name: "farmProductivityView", params: { reportUid: r.reportUid } }
            : null;
        case "anomaly_detection":
          return r.reportUid
            ? { name: "anomalyDetectionView", params: { reportUid: r.reportUid } }
            : null;
        case "yield_prediction":
          return r.reportUid
            ? { name: "yieldPredictionView", params: { id: r.reportUid } }
            : null;
        case "land_suitability":
          return r.reportId
            ? { name: "landSuitabilityDesignView", params: { id: r.reportId } }
            : null;
        default:
          return null;
      }
    },
    goManage() {
      this.$router.push({ name: "aiReportsManage" });
    },
  },
};
</script>

<style scoped>
@media (min-width: 960px) {
  .builders-col {
    flex-basis: 0 !important;
    flex-grow: 1 !important;
    max-width: 100% !important;
  }
}
.builder-card {
  border-radius: 14px;
  cursor: pointer;
  min-height: 150px;
  width: 100%;
  transition: box-shadow 0.15s ease, border-color 0.15s ease;
}
.builder-card:hover {
  border-color: #4caf50 !important;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}
.builder-disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
.builder-icon {
  width: 56px;
  height: 56px;
}
.dist-icon {
  width: 26px;
  height: 26px;
}
.dist-row + .dist-row {
  border-top: 1px solid #f0f0f0;
}
.recent-row + .recent-row {
  border-top: 1px solid #f0f0f0;
}
.recent-clickable {
  cursor: pointer;
  border-radius: 6px;
  transition: background 0.15s ease;
}
.recent-clickable:hover {
  background: #f6faf7;
}
</style>
